import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getVendorGameListByVendorApi } from '~/features/vendor-game/api/get-vendor-game-list-by-vendor.api';
import { getVendorGameListApi } from '~/features/vendor-game/api/get-vendor-game-list.api';
import { getVendorGameApi } from '~/features/vendor-game/api/get-vendor-game.api';

import type { VendorGame } from '~/features/vendor-game/types/vendor-game.type';

export interface VendorGameState {
  isLoading: boolean;
  list: VendorGame[];
  selectedVendorGame: VendorGame | null;
}

const initialState: VendorGameState = {
  isLoading: false,
  list: [],
  selectedVendorGame: null,
};

/**
 * 벤더 게임 목록 조회
 */
export const fetchVendorGameList = createAsyncThunk<VendorGame[]>(
  'vendorGame/fetchList',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getVendorGameListApi();
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * 벤더별 게임 목록 조회
 */
export const fetchVendorGameListByVendor = createAsyncThunk<VendorGame[], number>(
  'vendorGame/fetchListByVendor',
  async (vendorId, { rejectWithValue }) => {
    try {
      const { data } = await getVendorGameListByVendorApi(vendorId);
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * 벤더 게임 상세 조회
 */
export const fetchVendorGameDetail = createAsyncThunk<VendorGame, number>(
  'vendorGame/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await getVendorGameApi(id);
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const VendorGameSlice = createSlice({
  initialState,
  name: 'vendorGame',
  reducers: {
    setSelectedVendorGame: (state, action) => {
      state.selectedVendorGame = action.payload;
    },
    resetSelectedVendorGame: state => {
      state.selectedVendorGame = null;
    },
    resetVendorGame: state => {
      state.selectedVendorGame = null;
      state.list = [];
      state.isLoading = false;
    },
  },
  extraReducers: builder => {
    builder
      // fetchVendorGameList
      .addCase(fetchVendorGameList.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchVendorGameList.fulfilled, (state, action) => {
        state.list = Array.isArray(action.payload) ? action.payload : [];
        state.isLoading = false;
      })
      .addCase(fetchVendorGameList.rejected, state => {
        state.isLoading = false;
      })
      // fetchVendorGameListByVendor
      .addCase(fetchVendorGameListByVendor.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchVendorGameListByVendor.fulfilled, (state, action) => {
        state.list = Array.isArray(action.payload) ? action.payload : [];
        state.isLoading = false;
      })
      .addCase(fetchVendorGameListByVendor.rejected, state => {
        state.isLoading = false;
      })
      // fetchVendorGameDetail
      .addCase(fetchVendorGameDetail.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchVendorGameDetail.fulfilled, (state, action) => {
        state.selectedVendorGame = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchVendorGameDetail.rejected, state => {
        state.isLoading = false;
      });
  },
});

export const { setSelectedVendorGame, resetSelectedVendorGame, resetVendorGame } =
  VendorGameSlice.actions;
export default VendorGameSlice;
